// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLocation, useHistory } from 'react-router-dom';
import classNames from 'classnames';

import LogoPage from 'components/pages/LogoPage';
import { FocusGroup } from 'components/focus';

import DividedContent from 'components/common/DividedContent';
import WithLoading from 'components/common/WithLoading';
import Button from 'components/common/Button';
import Image from 'components/common/Image';

import { logoutParamountPlus, logoutAdobe } from 'actions/auth';

import useTracking from 'hooks/analytics';
import { useFocusManager } from 'hooks/focus';
import { Providers } from 'app-types';

import TextToSpeechManager from 'support/tts/TextToSpeechManager';
import { useParamountPlusDisplayName } from 'hooks/kitchenSink';
import styles from './Logout.module.scss';

const messages = {
  signOutLabel: {
    id: 'logout.signOutLabel',
    description: 'Sign out label',
  },
  signOutConfirmation: {
    id: 'logout.signOut.confirmation',
    description: 'Sign out confirmation text',
  },
  signOutTextAdobe: {
    id: 'logout.signOut.provider.adobe',
    description: 'Sign out name for adobe',
  },
  signOutButtonYes: {
    id: 'logout.signOut.button.yes',
    description: 'Sign out confirmation button',
  },
  signOutButtonNo: {
    id: 'logout.signOut.button.no',
    description: 'Sign out cancel button',
  },
};

const trackLogout = ({ tracker, provider }) => {
  const providerText =
    provider.toLowerCase() === Providers.ADOBE.toLowerCase() ? 'mvpd' : 'aa';
  const pageData = {
    pageName: `signout-${providerText}-success`,
    pageType: 'account',
  };
  tracker(pageData);
};

const Logout = () => {
  const intl = useIntl();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const provider = params.get('providers');

  const isAdobe = provider.toLowerCase() === Providers.ADOBE.toLowerCase();
  const isParamountPlus =
    provider.toLowerCase() === Providers.PARAMOUNT_PLUS.toLowerCase();

  const paramountPlusLogoUrl = useSelector(
    state => state.kitchenSink?.data?.providers?.paramountplus?.logoMediumUrl,
  );

  const paramountPlusDisplayName = useParamountPlusDisplayName();

  useEffect(() => {
    const getProviderTts = () => {
      if (isAdobe) {
        return intl.formatMessage(messages.signOutTextAdobe);
      }
      if (isParamountPlus) {
        return paramountPlusDisplayName;
      }
      return null;
    };

    const pageTts = intl.formatMessage(messages.signOutLabel);
    const descriptionTts = `${intl.formatMessage(
      messages.signOutConfirmation,
    )} ${getProviderTts()}`;

    TextToSpeechManager.add([pageTts, descriptionTts]);
  }, [paramountPlusDisplayName, intl, isAdobe, isParamountPlus]);

  const focusManager = useFocusManager();
  useEffect(() => {
    focusManager.setCurrentFocus('logout-cancel-button');
  }, [focusManager]);

  const history = useHistory();
  const dispatch = useDispatch();
  const { trackPage: tracker } = useTracking();
  const [loading, setLoading] = useState(false);
  const onConfirm = async () => {
    setLoading(true);
    if (isAdobe || isParamountPlus) {
      await dispatch(isAdobe ? logoutAdobe() : logoutParamountPlus());
      trackLogout({ tracker, provider });
    }
    setLoading(false);
    history.goBack();
  };

  const onCancel = () => {
    history.goBack();
  };

  return (
    <WithLoading loading={loading}>
      <LogoPage className="position-relative">
        <DividedContent title={intl.formatMessage(messages.signOutLabel)}>
          <div
            className={classNames(
              'd-flex flex-column',
              'h-100',
              'text-uppercase',
              styles.messageContent,
            )}
          >
            <span className={styles.message}>
              <FormattedMessage {...messages.signOutConfirmation} />
              {isParamountPlus && (
                <Image
                  className={classNames('ml-3', styles.providerLogo)}
                  src={paramountPlusLogoUrl}
                  alt={paramountPlusDisplayName}
                  data-testid="cbs-paramount-plus-logo"
                />
              )}
              {isAdobe && (
                <span data-testid="adobe-text">
                  {` ${intl.formatMessage(messages.signOutTextAdobe)}`}
                </span>
              )}
              <span>?</span>
            </span>
            <FocusGroup>
              <Button
                focusId="logout-confirm-button"
                data-testid="logout-confirm-button"
                ttsText={intl.formatMessage(messages.signOutButtonYes)}
                onClick={onConfirm}
                size="md"
                className={styles.button}
              >
                <FormattedMessage {...messages.signOutButtonYes} />
              </Button>
              <Button
                focusId="logout-cancel-button"
                data-testid="logout-cancel-button"
                ttsText={intl.formatMessage(messages.signOutButtonNo)}
                onClick={onCancel}
                size="md"
                className={styles.button}
              >
                <FormattedMessage {...messages.signOutButtonNo} />
              </Button>
            </FocusGroup>
          </div>
        </DividedContent>
      </LogoPage>
    </WithLoading>
  );
};

export default Logout;
